/**
 * FlowChatPanelV2 — the V2 chat shell (bead terra-design-v2-chat-yub.1;
 * body-branch wiring completed by bead terra-design-v2-chat-yub.5).
 *
 * Mounted by `ShellLayout.tsx` as the V2 panel container's
 * (`shell-next/panels/FlowPanels.tsx`) `chat` slot, with `onClose` wired to
 * `closePanel('chat')`. It is the only chat surface: the Actions dock has no
 * Chat tab. No feature flag gates the shell or the chat. The session state it renders is owned by
 * `FlowChatSessionsProvider`, and the stream is hosted once per session above
 * the view (see `useFlowChatSessionAdapter.ts`'s `SessionStreamHost`).
 *
 * List-vs-session is derived from `activeSessionId` exactly as V1's
 * `FlowChatPanel`/`FlowChatSessionView` split does — never mirrored into a
 * local `useState` (see `EnrollQueue/FlowChatPanel.tsx`'s own file header
 * for why). Both views get their non-visual logic from
 * `EnrollQueue/useFlowChatControllers.ts`, the SAME hooks V1 consumes — this
 * file is markup + local UI state only (coding-standards.md).
 *
 * `ChatSessionViewV2`'s `body` branch selection below mirrors V1's
 * `FlowChatSessionView` (`EnrollQueue/FlowChatPanel.tsx`, the `body =` chain)
 * 1:1 — same five branches, same order, same guard conditions — reusing
 * V1's OWN `AccountNotFoundState` and `FrozenMidCreateView` components
 * directly rather than restyled V2 copies (this bead's own "Compose With"
 * list cites them by their V1 names; neither has Glaze-specific visual
 * requirements in this epic's scope). Only the live-thread branch's leaf
 * renderer differs: `ChatThreadV2` (Glaze/assistant-ui) instead of
 * `KeystoneChatSurface` (V1's `Thread`/`Composer`). `ChatEmptyStateV2` (bead
 * .7's scope) is the pre-chat branch; `ChatComposerV2` (bead .6's scope) is
 * reused unconditionally except where V1 never shows an outer composer
 * either (account-not-found; frozen-mid-create already has its own
 * embedded input).
 */
import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import { Text } from '@clay-run/glaze/text';
import {
  useFlowChatListController,
  useFlowChatSessionController,
} from '../../EnrollQueue/useFlowChatControllers';
import {
  AccountNotFoundState,
  KEYSTONE_SURFACE,
  makeBoundPersona,
} from '../../../account-view/keystone/chat-panel-core';
import { DurableStreamRuntimeProvider } from '../../../account-view/keystone/DurableStreamRuntimeProvider';
import { FrozenMidCreateView } from '../../EnrollQueue/FrozenMidCreateView';
import { LibrarySidebar } from '../../../skills/LibrarySidebar';
import { ChatSessionListV2 } from './ChatSessionListV2';
import { ChatHeaderV2 } from './ChatHeaderV2';
import { ChatEmptyStateV2 } from './ChatEmptyStateV2';
import { ChatThreadV2 } from './ChatThreadV2';
import { ChatComposerV2, type ChatComposerV2Handle } from './ChatComposerV2';
import { closePanel } from '../panels/flow-panels-store';

export interface FlowChatPanelV2Props {
  /**
   * What the panel's Close affordance does. Defaults to `closePanel('chat')`:
   * the panel closes the PANEL it lives in, never the unrelated Actions dock.
   * `ShellLayout.tsx` passes the same thing explicitly.
   */
  onClose?: () => void;
}

const closeChatPanel = () => closePanel('chat');

export function FlowChatPanelV2({ onClose = closeChatPanel }: FlowChatPanelV2Props = {}) {
  const {
    sessions,
    activeSessionId,
    openSession,
    backToList,
    selectPersistedConversation,
    stopSession,
    pageAccountId,
    newSessionPickerOpen,
    closeNewSessionPicker,
    handleNewChatClick,
    pickNewSessionAccount,
  } = useFlowChatListController();
  const close = onClose;

  return (
    <div className="flex-1 min-w-0 min-h-0 flex flex-col" data-testid="flow-chat-panel-v2">
      {activeSessionId ? (
        <ChatSessionViewV2 key={activeSessionId} sessionId={activeSessionId} onBack={backToList} onClose={close} />
      ) : (
        <>
          <ChatHeaderV2
            state="list"
            pageAccountId={pageAccountId}
            newSessionPickerOpen={newSessionPickerOpen}
            onNewChatClick={handleNewChatClick}
            onPickNewSessionAccount={pickNewSessionAccount}
            onCloseNewSessionPicker={closeNewSessionPicker}
            onClose={close}
          />
          <ChatSessionListV2
            sessions={sessions}
            activeSessionId={activeSessionId}
            onSelect={openSession}
            onSelectPersisted={selectPersistedConversation}
            onStop={stopSession}
          />
        </>
      )}
    </div>
  );
}

interface ChatSessionViewV2Props {
  sessionId: string;
  onBack: () => void;
  onClose: () => void;
}

function ChatSessionViewV2({ sessionId, onBack, onClose }: ChatSessionViewV2Props) {
  const {
    entry,
    session,
    externalStream,
    draftText,
    setDraftText,
    isRunning,
    stopThisSession,
    accountId,
    accountLabel,
    conversationId,
    chatTitle,
    loadedPersonaName,
    controller: {
      accountNotFound,
      personas,
      skills,
      tools,
      pendingExample,
      consumePendingExample,
      prechatEpoch,
      handleExampleClick,
    },
    pageAccountId,
    selectConversation,
    selectDraftSession,
    startNewForAccount,
    startNewForDifferentAccount,
    handleInSessionNewChatClick,
    handleCopyLink,
    resumeWithMessage,
  } = useFlowChatSessionController(sessionId);

  // Library sidebar mount point (mounting fix — see `ChatComposerV2.tsx`'s file header). Owned
  // HERE, at the chat panel level, not inside `ChatComposerV2` — mirrors V1's
  // `chat-panel-core.tsx`'s `KeystoneChatSurface` exactly, where `librarySidebarOpen` and
  // `LibrarySidebar` both live in the component that renders the FULL chat-panel-height box.
  // `composerRef` is how a sidebar pick still reaches the composer's own draft state (see
  // `ChatComposerV2Handle`) without a second "is the library open" source of truth.
  const [librarySidebarOpen, setLibrarySidebarOpen] = useState(false);
  const composerRef = useRef<ChatComposerV2Handle>(null);

  // Persona/skills affordances (bead .15). `started` mirrors V1's
  // `KeystoneChatSurface`'s `messageCount > 0` — a committed conversation
  // always carries its own first message by the time this view reaches the
  // live-thread branch (the pre-chat branch, where `conversationId` is still
  // null, is the ONLY state with zero messages — see this file's header for
  // why `conversationId !== null` is an exact proxy here, not an
  // approximation). `composerPersona`/`onPickPersona` mirror
  // `chat-panel-core.tsx`'s own derivation and no-op contract exactly:
  // `pickedPersona` wins display (immediately after a pre-chat pick, before
  // the reload fetch returns); an existing conversation can never re-pick
  // (bound server-side), so `onPickPersona` becomes a no-op once
  // `conversationId` is set.
  const started = conversationId !== null;
  const composerPersona = session.persona ?? (loadedPersonaName ? makeBoundPersona(loadedPersonaName) : null);
  const handlePickPersona = conversationId ? () => {} : session.pickPersona;

  // Shouldn't happen in practice — a viewed session is never deleted out
  // from under its own viewer today (no delete-session action exists; same
  // guard V1's `FlowChatSessionView` has).
  if (!entry) return null;

  let body: ReactNode;
  // V1 never shows its outer composer for these two branches either
  // (account-not-found has nothing to compose for; frozen-mid-create
  // already renders its own embedded textarea/send inside
  // `FrozenMidCreateView`) — see this file's header comment.
  let showComposer = true;
  // Bead .6 wiring. Pre-chat (no conversationId yet) routes a send through
  // `session.handleSend` (lazy create, via `sendFirstMessage` — same
  // primitive `entry.stopped`'s mid-create resume above uses). A live
  // conversation routes through the composed stream's OWN `send`:
  // `useFlowChatSessionAdapter`'s own doc comment says `externalStream`
  // always carries a `send` once non-null — either the live host's own, or
  // the resume-wrapper for a stopped-but-committed session — so this one
  // call site covers both "live" and "resume" without branching here.
  // `isStreaming` is `isRunning` (creating OR stream running), exactly as
  // V1's composer-Stop-accessory derives it (`FlowChatPanel.tsx`'s
  // `composerStopAccessory`) — never re-derived locally (coding-
  // standards.md: one derivation, shared by V1 and V2).
  let handleComposerSubmit: (text: string) => void = (text) => {
    void session.handleSend(text);
  };
  let composerSendDisabled = false;

  if (accountNotFound) {
    body = <AccountNotFoundState />;
    showComposer = false;
  } else if (conversationId && entry.stopped && !externalStream) {
    // Wave 2 review finding, ported verbatim from V1 (`FlowChatPanel.tsx`'s
    // own comment on this exact branch) — a session that's stopped, has a
    // conversationId, but has never published a stream snapshot. Treat as
    // resumable via `resumeWithMessage` (NOT `session.handleSend`, which
    // no-ops once `conversationId` is set).
    body = (
      <FrozenMidCreateView
        pendingFirstMessage={entry.pendingFirstMessage}
        onResume={(text) => {
          resumeWithMessage(text);
        }}
      />
    );
    showComposer = false;
  } else if (conversationId) {
    if (!externalStream) {
      // Committed but the host hasn't published its first snapshot yet — a
      // brief window right after `commitConversationId`. Never fall through
      // to mounting `DurableStreamRuntimeProvider` with no `externalStream`
      // here: that would open a SECOND EventSource for this conversation
      // (its own documented no-op-vs-real-stream contract).
      body = (
        <div className="flex-1 flex items-center justify-center text-[12.5px] text-content-tertiary">
          Connecting…
        </div>
      );
      // Nothing to send to yet — the stream hasn't published its first
      // snapshot (see the comment above). Composer stays visible (typing
      // is never blocked) but can't submit into this brief window.
      composerSendDisabled = true;
    } else {
      body = (
        <DurableStreamRuntimeProvider key={conversationId} conversationId={conversationId} externalStream={externalStream}>
          <ChatThreadV2 accountId={accountId} />
        </DurableStreamRuntimeProvider>
      );
      handleComposerSubmit = (text) => {
        void externalStream.send(text);
      };
    }
  } else if (entry.stopped) {
    // Stopped mid-create (no conversationId was ever committed). Sending
    // here calls `session.handleSend`, which routes to the store's
    // `sendFirstMessage` — the SAME "sending is the resume" primitive that
    // also clears `stopped`.
    body = (
      <FrozenMidCreateView
        pendingFirstMessage={entry.pendingFirstMessage}
        onResume={(text) => {
          void session.handleSend(text);
        }}
      />
    );
    showComposer = false;
  } else {
    body = <ChatEmptyStateV2 accountLabel={accountLabel} onSelectSuggestion={handleExampleClick} />;
  }

  return (
    <>
      <ChatHeaderV2
        state="thread"
        accountId={accountId}
        accountLabel={accountLabel}
        chatTitle={chatTitle}
        conversationId={conversationId}
        sessionId={sessionId}
        pageAccountId={pageAccountId}
        onBack={onBack}
        onSelectConversation={selectConversation}
        onSelectDraftSession={selectDraftSession}
        onStartNewForAccount={startNewForAccount}
        onStartNewForDifferentAccount={startNewForDifferentAccount}
        onInSessionNewChatClick={handleInSessionNewChatClick}
        onCopyLink={handleCopyLink}
        onClose={onClose}
      />
      {/* Positioned ancestor for `LibrarySidebar` (mounting fix) — its rail/panel are
          `absolute inset-y-0`, which needs a `relative` box spanning the FULL area below the
          header (mirrors V1's `chat-panel-core.tsx`'s `KeystoneChatSurface` return exactly: the
          header there also lives one level up, in `FlowChatPanel.tsx`). */}
      <div className="relative flex flex-1 min-h-0 overflow-hidden">
        <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
          {entry.stopped && (
            <Text size="xs" variant="tertiary" className="shrink-0 border-b border-border-secondary px-3.5 py-2">
              This chat was stopped — send a message to pick it back up.
            </Text>
          )}
          {/* Inline create error (non-account-not-found) — ported from V1's
              `PreChatSurface` when the V1 dock chat was deleted. The
              account-not-found case is the full-pane `AccountNotFoundState`
              above; anything else surfaces here without orphaning local state
              (the session already cleared the pending message on failure). */}
          {session.createError && session.createError !== 'Account not found' && (
            <div role="alert" className="shrink-0 px-3.5 pt-3">
              <Text size="xs" variant="critical">
                {session.createError}
              </Text>
            </div>
          )}
          {body}
          {showComposer && (
            <ChatComposerV2
              // Remount key mirrors V1's `PreChatSurface key={prechatEpoch}` (bumped by
              // `controller.handleNew`) — see that component's own doc comment for why clearing
              // `pendingExample` alone isn't enough. Flow doesn't wire a `handleNew` trigger into
              // this epic's scope, but the key costs nothing and keeps this composer
              // byte-identical to the controller's contract if one is added later.
              key={prechatEpoch}
              ref={composerRef}
              sessionId={sessionId}
              draftText={draftText}
              onDraftChange={setDraftText}
              onSubmit={handleComposerSubmit}
              onStop={stopThisSession}
              isStreaming={isRunning}
              isBusy={false}
              sendDisabled={composerSendDisabled}
              pendingExample={pendingExample}
              onConsumePendingExample={consumePendingExample}
              personas={personas}
              skills={skills}
              persona={composerPersona}
              started={started}
              onPickPersona={handlePickPersona}
              onOpenLibrary={() => setLibrarySidebarOpen(true)}
              onCloseLibrary={() => setLibrarySidebarOpen(false)}
            />
          )}
        </div>

        {showComposer && (
          <>
            {/* Click-outside backdrop — only when the sidebar is open. Panel-scoped (`absolute`,
                not `fixed`) — same as V1's `chat-panel-core.tsx` backdrop (`:476`). Same
                aria-label the sidebar's OWN rail-toggle button carries once open
                (`LibrarySidebar.tsx`'s internal `open ? 'Close…' : 'Open…'`), so a query for it is
                ambiguous by design — same as V1 today (see
                `KeystoneChatPanel.parent-context.test.tsx`'s
                `getAllByLabelText('Close toolkit sidebar')` precedent). */}
            {librarySidebarOpen && (
              <button
                type="button"
                aria-label="Close toolkit sidebar"
                onClick={() => setLibrarySidebarOpen(false)}
                className="absolute inset-0 z-10 cursor-default"
              />
            )}
            <LibrarySidebar
              surface={KEYSTONE_SURFACE}
              persona={composerPersona}
              onPickPersona={(p) => composerRef.current?.pickPersonaFromSidebar(p)}
              onPickSkill={(s) => composerRef.current?.pickSkillFromSidebar(s)}
              tools={tools ?? []}
              open={librarySidebarOpen}
              onOpenChange={setLibrarySidebarOpen}
            />
          </>
        )}
      </div>
    </>
  );
}
